import { useEffect, useState } from "react";
import { api, printAuthedPdf } from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";
import ImageUploader from "@/components/ImageUploader";
import InvoicePreview from "@/components/admin/InvoicePreview";
import InvoiceReasonsEditor from "@/components/admin/InvoiceReasonsEditor";
import { Check, Printer, Loader2 } from "lucide-react";

const COMPANY_FIELDS = [
    ["invoice_company_name", "Emri i kompanisë"], ["invoice_company_nipt", "NIPT"],
    ["invoice_company_address", "Adresa"], ["invoice_company_city", "Qyteti"],
    ["invoice_company_phone", "Telefon"], ["invoice_company_email", "Email"],
    ["invoice_company_iban", "IBAN"], ["invoice_company_bank", "Banka"],
];
const COLUMNS = [
    ["invoice_show_part_number", "Kodi"], ["invoice_show_name", "Përshkrimi"],
    ["invoice_show_description", "Detaje"], ["invoice_show_category", "Kategoria"],
    ["invoice_show_qty", "Sasia"], ["invoice_show_unit_price", "Çmimi"],
    ["invoice_show_line_total", "Totali"],
];
const SAMPLE_SALE = {
    id: "sample0001", created_at: new Date().toISOString(), customer_name: "Klient Shembull", customer_phone: "069 000 0000",
    items: [
        { part_number: "GTB1749V", name: "Turbocharger 787556", description: "VW 2.0 TDI", category: "TURBOCHARGER", qty: 1, unit_price: 420, line_total: 420 },
        { part_number: "1000-010-497", name: "CHRA", description: "", category: "CHRA", qty: 2, unit_price: 95, line_total: 190 },
    ],
    subtotal: 610, discount_percent: 5, discount_amount: 30.5, total: 579.5,
    service_note: "Kontroll pas 1000 km.",
};

const Section = ({ title, children }) => (
    <section className="border border-slate-200 p-4">
        <div className="label-kicker text-slate-600 mb-3">{title}</div>
        {children}
    </section>
);

export default function InvoiceTab() {
    const { content, updateContent } = useSiteContent();
    const [draft, setDraft] = useState(content);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [lastSale, setLastSale] = useState(null);
    const [previewType, setPreviewType] = useState("preventiv");

    useEffect(() => { setDraft(content); }, [content]);
    useEffect(() => {
        api.get("/admin/sales", { params: { limit: 1 } }).then(({ data }) => setLastSale(data[0] || null)).catch(() => {});
    }, []);

    const set = (patch) => { setDraft((d) => ({ ...d, ...patch })); setSaved(false); };
    const dirty = JSON.stringify(draft) !== JSON.stringify(content);
    const save = async () => {
        setSaving(true);
        try {
            await updateContent({
                ...content,
                ...draft,
                invoice_failure_reasons: (draft.invoice_failure_reasons || []).map((r) => r.trim()).filter(Boolean),
                invoice_services: (draft.invoice_services || []).map((r) => r.trim()).filter(Boolean),
                invoice_paid_services: (draft.invoice_paid_services || []).map((r) => r.trim()).filter(Boolean),
            });
            setSaved(true);
        } catch (e) {
            alert(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Ruajtja dështoi");
        } finally {
            setSaving(false);
        }
    };
    const rate = draft.currency_lek_per_eur || 100;
    const svcList = (draft.invoice_services || []).slice(0, 3);
    const svcLines = (draft.invoice_paid_services || []).map((n) => ({ name: n, qty: 1, price_lek: (draft.invoice_service_prices || {})[n] || 0 })).filter((l) => l.price_lek > 0).map((l) => ({ ...l, price_eur: Math.round((l.price_lek / rate) * 100) / 100, line_eur: Math.round((l.price_lek / rate) * 100) / 100 }));
    const svcTotal = svcLines.reduce((a, l) => a + l.line_eur, 0);
    const previewSale = { ...SAMPLE_SALE, failure_reasons: (draft.invoice_failure_reasons || []).slice(0, 3), services: svcList, service_lines: svcLines, services_total: svcTotal, total: SAMPLE_SALE.total + svcTotal };

    return (
        <div data-testid="admin-invoice-tab" className="grid xl:grid-cols-[1fr_1.1fr] gap-6 items-start">
            <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2 sticky top-0 z-10 bg-white py-2 border-b border-slate-200">
                    <div>
                        <div className="font-display font-black uppercase text-lg">Modeli i faturës</div>
                        <div className="text-xs text-slate-500">Ndryshimet aplikohen te PDF, Print dhe pamja e faturës.</div>
                    </div>
                    <div className="flex gap-2">
                        {lastSale && (
                            <button type="button" onClick={() => printAuthedPdf(`/admin/sales/${lastSale.id}/pdf`, { inline: true }).catch((e) => alert(e.message))} disabled={dirty} title={dirty ? "Ruaj ndryshimet fillimisht" : "Printo faturën e fundit si test"} className="btn-outline !py-1.5 !text-xs disabled:opacity-40" data-testid="invoice-test-print">
                                <Printer className="w-3.5 h-3.5" /> Test print
                            </button>
                        )}
                        <button type="button" onClick={save} disabled={!dirty || saving} className="btn-primary !py-1.5 !text-xs disabled:opacity-40" data-testid="invoice-settings-save">
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            {saved && !dirty ? "U ruajt" : "Ruaj ndryshimet"}
                        </button>
                    </div>
                </div>

                <Section title="Kreu i dokumentit (titulli)">
                    <div className="grid md:grid-cols-2 gap-4">
                        {[["invoice", "Faturë"], ["preventiv", "Preventiv"]].map(([k, label]) => (
                            <div key={k} className="space-y-2" onFocus={() => setPreviewType(k)}>
                                <label className="label-kicker block">Titulli · {label}</label>
                                <input type="text" value={draft[`invoice_title_${k}`] || ""} onChange={(e) => set({ [`invoice_title_${k}`]: e.target.value })} className="input-field !py-1.5" data-testid={`invoice-title-${k}`} />
                                <label className="label-kicker block">Teksti nën titull · {label}</label>
                                <textarea rows="2" value={draft[`invoice_subtitle_${k}`] || ""} onChange={(e) => set({ [`invoice_subtitle_${k}`]: e.target.value })} placeholder={k === "preventiv" ? "p.sh. Oferta vlen 7 ditë" : "p.sh. Faturë tatimore"} className="input-field !text-xs" data-testid={`invoice-subtitle-${k}`} />
                            </div>
                        ))}
                    </div>
                </Section>

                <Section title="Të dhënat e kompanisë">
                    <div className="grid md:grid-cols-2 gap-3">
                        {COMPANY_FIELDS.map(([key, label]) => (
                            <div key={key}>
                                <label className="label-kicker mb-1 block">{label}</label>
                                <input type="text" value={draft[key] || ""} onChange={(e) => set({ [key]: e.target.value })} className="input-field !py-1.5" data-testid={`invoice-${key.replace(/_/g, "-")}`} />
                            </div>
                        ))}
                    </div>
                </Section>

                <Section title="Logo & sfondi">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="label-kicker mb-1 block">Logo e faturës</label>
                            <ImageUploader value={draft.invoice_logo_url} onChange={(url) => set({ invoice_logo_url: url || "" })} testId="invoice-logo-uploader" recommended="400×120px · PNG transparent" />
                        </div>
                        <div>
                            <label className="label-kicker mb-1 block">Sfondi / watermark</label>
                            <ImageUploader value={draft.invoice_background_url} onChange={(url) => set({ invoice_background_url: url || "" })} testId="invoice-bg-uploader" recommended="1240×1754px (A4)" />
                            {draft.invoice_background_url && (
                                <>
                                    <label className="label-kicker mt-3 mb-1 block">Opaciteti: {Math.round((draft.invoice_background_opacity ?? 0.08) * 100)}%</label>
                                    <input type="range" min="0" max="40" value={Math.round((draft.invoice_background_opacity ?? 0.08) * 100)} onChange={(e) => set({ invoice_background_opacity: parseInt(e.target.value, 10) / 100 })} className="w-full accent-[hsl(var(--primary))]" data-testid="invoice-bg-opacity" />
                                </>
                            )}
                        </div>
                    </div>
                </Section>

                <Section title="Kolonat në faturë">
                    <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        {COLUMNS.map(([key, label]) => (
                            <label key={key} className="flex items-center gap-2 cursor-pointer border border-slate-200 px-3 py-2 hover:bg-slate-50" data-testid={`invoice-col-${key.replace(/_/g, "-")}`}>
                                <input type="checkbox" checked={Boolean(draft[key])} onChange={(e) => set({ [key]: e.target.checked })} className="accent-[hsl(var(--primary))]" />
                                <span>{label}</span>
                            </label>
                        ))}
                    </div>
                </Section>

                <Section title="Arsyet e prishjes (kutitë ✓ poshtë totalit)">
                    <InvoiceReasonsEditor
                        title={draft.invoice_reasons_title || ""}
                        reasons={draft.invoice_failure_reasons || []}
                        onTitle={(v) => set({ invoice_reasons_title: v })}
                        onReasons={(list) => set({ invoice_failure_reasons: list })}
                    />
                </Section>

                <Section title="Shërbimet që duhen bërë (kutitë ✓ poshtë arsyeve)">
                    <InvoiceReasonsEditor
                        title={draft.invoice_service_note_title || ""}
                        reasons={draft.invoice_services || []}
                        onTitle={(v) => set({ invoice_service_note_title: v })}
                        onReasons={(list) => set({ invoice_services: list })}
                        testIdPrefix="invoice-service"
                        placeholder="Shërbim i ri, p.sh. Ndërrim guarnicioni i turbos"
                        hint="Rekomandime për klientin: te fatura shfaqen vetëm ato që shënon ✓, plus shënimi shtesë."
                    />
                </Section>

                <Section title="Shërbime me pagesë (Add service te Sales)">
                    <InvoiceReasonsEditor
                        title=""
                        hideTitle
                        reasons={draft.invoice_paid_services || []}
                        onTitle={() => {}}
                        onReasons={(list) => set({ invoice_paid_services: list })}
                        prices={draft.invoice_service_prices || {}}
                        onPrices={(p) => set({ invoice_service_prices: p })}
                        testIdPrefix="invoice-paid-service"
                        placeholder="Shërbim me pagesë, p.sh. Montim turbo"
                        hint="Çmimi në Lek. Këto zgjidhen te Sales → Add service (edhe pa produkt), dalin si rresht 'SHËRBIM' në faturë dhe shtohen në total pa zbritje."
                    />
                </Section>

                <Section title="Kuponi 80mm · teksti në fund">
                    <label className="block mb-3"><span className="text-xs text-slate-500 mb-1 block">Falënderimi</span>
                        <input value={draft.receipt_footer ?? ""} onChange={(e) => set({ receipt_footer: e.target.value })} placeholder="Faleminderit për blerjen!" className="input-field" data-testid="receipt-footer-text" /></label>
                    <label className="block"><span className="text-xs text-slate-500 mb-1 block">Garancia (lëre bosh për ta hequr)</span>
                        <textarea rows="2" value={draft.warranty_text ?? ""} onChange={(e) => set({ warranty_text: e.target.value })} placeholder="Garanci 6 muaj sipas kushteve." className="input-field" data-testid="receipt-warranty-text" /></label>
                </Section>
                <Section title="Teksti në fund (kushte, garanci, falënderim)">
                    <textarea rows="3" value={draft.invoice_footer_text || ""} onChange={(e) => set({ invoice_footer_text: e.target.value })} className="input-field" data-testid="invoice-footer-text" />
                </Section>
            </div>

            <div className="xl:sticky xl:top-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="label-kicker text-slate-500">Pamja paraprake (shembull)</div>
                    <select value={previewType} onChange={(e) => setPreviewType(e.target.value)} className="input-field !py-1 !text-xs !w-auto" data-testid="invoice-preview-type">
                        <option value="invoice">Faturë</option>
                        <option value="preventiv">Preventiv</option>
                    </select>
                </div>
                <div className="border border-slate-300 shadow-sm overflow-hidden" data-testid="invoice-settings-preview">
                    <InvoicePreview sale={previewSale} content={draft} docType={previewType} />
                </div>
            </div>
        </div>
    );
}
